import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Calendar, ShieldCheck, Heart, ArrowRight, Play } from "lucide-react";
import { fetchPages, fetchBehandlinger } from "../lib/cms";
import { videoer, virksomhedsinfo } from "../data";
import VideoModal from "../components/VideoModal";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [behandlinger, setBehandlinger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    async function loadData() {
      const pages = await fetchPages();
      const indexPage = pages.find((p) => p.page_slug === "index");
      setPageData(indexPage);

      const list = await fetchBehandlinger();
      setBehandlinger(list);
      setLoading(false);
    }
    loadData();
  }, []);

  // Sæt sidetitel og meta-beskrivelse
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || "RG Fodpleje - Professionel fodpleje i Odense N";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "Professionel fodpleje i Odense N.");
      }
    }
  }, [pageData]);

  return (
    <div className="bg-[#FAF8F5] text-[#2D2A26] min-h-screen">
      {/* Hero Sektion med Video og Velkomst */}
      <section className="relative overflow-hidden py-12 lg:py-24 border-b border-[#EAE5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Venstre side: Tekst og CTA */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8EFEA] text-[#2E4233] text-xs font-semibold tracking-wider uppercase">
                <ShieldCheck className="h-4 w-4" />
                <span>Uddannet & SADF-registreret fodplejer</span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-[#2D2A26] leading-tight">
                RG Fodpleje, <br />
                <span className="text-[#4A5D4E] lg:whitespace-nowrap">fodbehandling i Odense</span>
              </h1>

              {/* Den præcise forsidetekst */}
              <div className="text-lg sm:text-xl text-[#2D2A26] leading-relaxed space-y-4">
                <p className="font-normal">
                  Hos RG Fodpleje er der fokus på kvalitet, ro og nærvær. Her bliver du mødt med god tid.
                </p>
                <p className="font-medium text-[#2D2A26]">
                  Hver fodbehandling udføres grundigt og professionelt, med opmærksomhed på både velvære og sundhed.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="https://rg-fodpleje.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md hover:shadow-lg text-center flex items-center justify-center gap-2 group"
                >
                  <Calendar className="h-5 w-5" />
                  <span>Book tid online</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/behandlinger"
                  className="border border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-[#FAF8F5] px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 text-center"
                >
                  Se behandlinger & priser
                </Link>
              </div>
            </div>

            {/* Højre side: Elegant video-afspiller */}
            <div className="lg:col-span-5">
              <div 
                onClick={() => setIsModalOpen(true)}
                className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-stone-100 aspect-[3/4] max-w-md mx-auto cursor-pointer group"
              >
                <video
                  id="hero-video"
                  src={videoer[0].url} // RG Fodpleje video 5 justeret.mp4
                  poster="https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/6ff38804-473d-4647-91a8-2151bb31a68d.JPEG" // IMG_0551.JPEG
                  className="h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
                {/* Dark overlay on hover */}
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center" />
                
                {/* Play button in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-white/90 group-hover:bg-white text-[#4A5D4E] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <Play className="h-6 w-6 fill-current ml-1" />
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 bg-black/40 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                  Klinikstemning
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Værdier / Hvorfor vælge os */}
      <section className="py-16 bg-[#F3EFEA] border-b border-[#EAE5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2D2A26]">
              Fokus på dine fødders sundhed
            </h2>
            <p className="text-[#2D2A26] mt-3 font-normal">
              Hos RG Fodpleje får du en professionel og behagelig oplevelse, hvor hygiejne og dine behov altid er i højsædet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#FAF8F5] p-8 rounded-2xl shadow-sm border border-[#EAE5DF] space-y-4">
              <div className="h-12 w-12 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center">
                <Heart className="h-6 w-6" />
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2D2A26]">Ro & Nærvær</h3>
              <p className="text-[#2D2A26] text-sm leading-relaxed font-normal">
                Her er der sat god tid af til hver enkelt behandling. Du slipper for stress og jag, og kan slappe helt af i rolige og hyggelige omgivelser.
              </p>
            </div>

            <div className="bg-[#FAF8F5] p-8 rounded-2xl shadow-sm border border-[#EAE5DF] space-y-4">
              <div className="h-12 w-12 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2D2A26]">Høj Hygiejnestandard</h3>
              <p className="text-[#2D2A26] text-sm leading-relaxed font-normal">
                Alt værktøj steriliseres i en autoklave efter brug og opbevares i forseglede poser. Som medlem af SADF overholdes alle gældende retningslinjer.
              </p>
            </div>

            <div className="bg-[#FAF8F5] p-8 rounded-2xl shadow-sm border border-[#EAE5DF] space-y-4">
              <div className="h-12 w-12 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2D2A26]">Faglig Ekspertise</h3>
              <p className="text-[#2D2A26] text-sm leading-relaxed font-normal">
                Uddannet fodplejer med bred viden om behandling af hård hud, ligtorne, negleproblemer og fodvorter. Du får rådgivning med hjem til daglig pleje.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Behandlinger Oversigt */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2D2A26]">
                Vores Behandlinger
              </h2>
              <p className="text-[#2D2A26] mt-2 font-normal">
                Professionelle behandlinger skræddersyet til dine fødders behov.
              </p>
            </div>
            <Link
              to="/behandlinger"
              className="text-[#4A5D4E] hover:text-[#3B4C3E] font-medium flex items-center gap-1 group transition-colors"
            >
              <span>Se alle priser og detaljer</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {behandlinger.slice(0, 4).map((b) => (
              <div
                key={b.treatment_slug}
                className="bg-white rounded-2xl overflow-hidden border border-[#EAE5DF] shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                  <img
                    src={b.image && b.image[0]}
                    alt={b.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-[#4A5D4E]">
                    {b.price},00 kr.
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
                  <div className="space-y-2">
                    <h3 className="font-serif text-lg font-semibold text-[#2D2A26] group-hover:text-[#4A5D4E] transition-colors">
                      {b.title}
                    </h3>
                    <div
                      className="text-sm text-[#2D2A26] line-clamp-3 font-normal leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: b.description }}
                    />
                  </div>
                  <Link
                    to={
                      b.treatment_slug === "behandling-af-ligtorne" ||
                      b.treatment_slug?.includes("ligtorn") ||
                      b.title?.toLowerCase().includes("ligtorn")
                        ? "/behandling-af-ligtorn"
                        : "/behandlinger"
                    }
                    className="text-xs font-medium text-[#4A5D4E] hover:text-[#3B4C3E] flex items-center gap-1 pt-2 border-t border-[#FAF8F5]"
                  >
                    <span>Læs mere</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galleri / Klinikstemning Sektion */}
      <section className="py-16 bg-[#F3EFEA] border-t border-b border-[#EAE5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-serif text-3xl font-semibold text-[#2D2A26]">
              Billeder fra klinikken
            </h2>
            <p className="text-[#2D2A26] mt-2 font-normal">
              Tag et kig indenfor i de lyse, rene og hyggelige lokaler i Odense N.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {galleriBilleder.map((img, idx) => (
              <div
                key={idx}
                className="aspect-square rounded-xl overflow-hidden border-2 border-white shadow-sm hover:scale-102 transition-transform duration-300 bg-stone-100"
              >
                <img
                  src={img}
                  alt={`Klinikbillede ${idx + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-20 bg-[#4A5D4E] text-[#FAF8F5] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 mix-blend-overlay">
          <div className="absolute inset-0 bg-[radial-gradient(#FAF8F5_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-6">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Giv dine fødder den pleje, de fortjener
          </h2>
          <p className="text-[#FAF8F5] text-lg max-w-2xl mx-auto font-normal leading-relaxed">
            Book nemt og hurtigt din næste fodbehandling online. Jeg glæder mig til at byde dig velkommen i klinikken og give dine fødder fornyet energi.
          </p>
          <div className="pt-4">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#4A5D4E] px-8 py-4 rounded-full text-base font-semibold tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              <Calendar className="h-5 w-5" />
              <span>Book din tid her</span>
            </a>
          </div>
          <p className="text-xs text-[#C2D1C5]">
            Eller ring på tlf. <span className="font-semibold">{virksomhedsinfo.telefon}</span> for tidsbestilling.
          </p>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={videoer[0].url}
        title="Klinikstemning - RG Fodpleje"
      />
    </div>
  );
}

const galleriBilleder = [
  "https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/fodbehandling-med-neglelak.jpeg",
  "https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/behandling-af-fodvorte.jpeg",
  "https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/fodbehandling-odense.jpeg",
  "https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/1b0e52f5-3c4b-4fbb-8990-a60b7cc063da.JPEG"
];
